import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Check, Loader2 } from "lucide-react";
import { Meta } from "../components/SEO";
import { company } from "../data/company";
import { getUtm } from "../utils/utm";
import { trackConversion } from "../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/contact`, { ...form, utm: getUtm() });
      trackConversion("contact", 30, { subject: form.subject });
      setDone(true);
    } catch {
      setError("Er ging iets mis. Probeer het opnieuw of bel ons direct.");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Meta
        title="Contact — Aannemer Maassluis | Koning Aannemersbedrijf"
        description="Contact met Koning Aannemersbedrijf in Maassluis: bel 06 - 35 65 06 10, stuur een WhatsApp of vraag online een offerte aan. Reactie binnen één werkdag."
      />

      <section className="pt-24 pb-12 md:pt-32 md:pb-16 border-b border-koning-border" data-testid="contact-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="eyebrow mb-5">Contact</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-white tracking-tight max-w-4xl">
            Laten we <span className="italic text-koning-gold">kennismaken</span>.
          </h1>
          <p className="mt-6 text-koning-muted max-w-2xl leading-relaxed">
            Wij reageren binnen één werkdag op iedere aanvraag. Bel direct, stuur een bericht of vraag een vrijblijvende offerte aan.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28" data-testid="contact-grid">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-[1fr_1.2fr] gap-14">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-white mb-8">Bereik ons direct</h2>
            <ul className="space-y-7">
              <li className="flex gap-4">
                <div className="w-12 h-12 border border-koning-gold flex items-center justify-center text-koning-gold flex-shrink-0"><Phone size={18} /></div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted">Telefonisch</div>
                  <a href={company.phoneHref} className="font-display text-xl md:text-2xl text-white hover:text-koning-gold transition-colors" data-testid="contact-phone">{company.phone}</a>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-12 h-12 border border-koning-gold flex items-center justify-center text-koning-gold flex-shrink-0"><Mail size={18} /></div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted">E-mail</div>
                  <a href={company.emailHref} className="font-display text-xl text-white hover:text-koning-gold transition-colors break-all" data-testid="contact-email">{company.email}</a>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-12 h-12 border border-koning-gold flex items-center justify-center text-koning-gold flex-shrink-0"><MapPin size={18} /></div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted">Vestiging</div>
                  <div className="text-white text-base mt-1">
                    {company.address.street && <>{company.address.street}<br /></>}
                    {company.address.postalCode} {company.address.city}
                  </div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-12 h-12 border border-koning-gold flex items-center justify-center text-koning-gold flex-shrink-0"><Clock size={18} /></div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-koning-muted">Openingstijden</div>
                  <ul className="text-white text-sm mt-1 space-y-0.5">
                    {company.hours.map((h) => (
                      <li key={h.day} className="flex gap-3"><span className="text-koning-muted w-44">{h.day}</span><span>{h.time}</span></li>
                    ))}
                  </ul>
                </div>
              </li>
            </ul>

            <div className="mt-10 border border-koning-border p-5">
              <div className="text-xs tracking-[0.24em] uppercase text-koning-muted">Bedrijfsgegevens</div>
              <div className="text-sm text-white mt-2 space-y-1">
                <div>KvK: <span className="text-koning-muted">{company.kvk}</span></div>
                <div>BTW: <span className="text-koning-muted">{company.btw}</span></div>
              </div>
            </div>
          </div>

          <div>
            {done ? (
              <div className="border border-koning-gold p-10 text-center" data-testid="contact-success">
                <div className="w-14 h-14 mx-auto border border-koning-gold flex items-center justify-center mb-5">
                  <Check className="text-koning-gold" size={24} />
                </div>
                <h3 className="font-display text-2xl text-white mb-2">Dank u — bericht ontvangen</h3>
                <p className="text-koning-muted text-sm">We reageren binnen één werkdag.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-koning-surface border border-koning-border p-8 md:p-12" data-testid="contact-form">
                <h2 className="font-display text-2xl md:text-3xl text-white mb-6">Stuur ons een bericht</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="koning-label">Naam *</label>
                    <input required className="koning-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" />
                  </div>
                  <div>
                    <label className="koning-label">Telefoon</label>
                    <input className="koning-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-form-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="koning-label">E-mail *</label>
                    <input type="email" required className="koning-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="koning-label">Onderwerp</label>
                    <input className="koning-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-form-subject" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="koning-label">Bericht *</label>
                    <textarea required rows="4" className="koning-input resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-form-message" />
                  </div>
                </div>
                {error && <div className="mt-4 text-red-400 text-sm">{error}</div>}
                <div className="mt-8 flex justify-end">
                  <button type="submit" className="btn-gold disabled:opacity-50" disabled={submitting} data-testid="contact-form-submit">
                    {submitting ? <><Loader2 className="animate-spin" size={16}/> Verzenden…</> : "Bericht verzenden"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
